// src/components/doctor/toothchart/ChartBig.tsx
import React, { useRef, useState } from "react";
import ToothChart from "./ToothChart";
import "./ChartBig.css";
import type { ToothEvent } from "../../../types/domain";

export interface ChartBigProps {
  toothStates?: Record<string, string>;
  surfacesMap?: Record<string, unknown>;
  flagsMap?: Record<string, unknown>;
  movementsMap?: Record<string, unknown>;
  selectedTeeth?: string[];
  onToothClick?: (toothId: string) => void;
  isLocked?: boolean;
  animateReplay?: boolean;
  title?: string;
  fitX?: number;
  fitY?: number;
  fitS?: number;
  /** If true, chart will scale to fill container automatically. */
  autoFit?: boolean;
  /** Chart history for live Clinical view (toothEvents from chart_history) */
  toothEvents?: ToothEvent[] | null;
}

export default function ChartBig({
  toothStates = {},
  surfacesMap = {},
  flagsMap = {},
  movementsMap = {},
  selectedTeeth = [],
  onToothClick,
  isLocked = false,
  animateReplay = false,
  title = "Full Chart View",
  fitX,
  fitY,
  fitS,
  autoFit = false,
  toothEvents = null,
}: ChartBigProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // If autoFit is enabled, start with scale 1 so ToothChart can handle sizing.
  const [scale, setScale] = useState(autoFit ? 1 : 1.8);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const Y_OFFSET = 230; // Fixed vertical offset for proper positioning in History section

  /* ======================================================
     ZOOM HANDLING
  ====================================================== */

  const zoomIn = () => setScale((s) => Math.min(s + 0.15, 3.5));
  const zoomOut = () => setScale((s) => Math.max(s - 0.15, 0.7));
  const resetView = () => {
    setScale(autoFit ? 1 : 1.8);
    setPan({ x: 0, y: 0 });
  };

  /* ======================================================
     PAN HANDLING
     ====================================================== */

  const handlePanStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // left mouse only
    setIsPanning(true);
    setStartPan({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    });
  };

  const stopPanning = () => {
    setIsPanning(false);
  };

  const doPan = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPanning) return;
    setPan({
      x: e.clientX - startPan.x,
      y: e.clientY - startPan.y
    });
  };

  /* ======================================================
     TOUCH SUPPORT
  ====================================================== */

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      setIsPanning(true);
      setStartPan({
        x: t.clientX - pan.x,
        y: t.clientY - pan.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isPanning || e.touches.length !== 1) return;
    const t = e.touches[0];
    setPan({
      x: t.clientX - startPan.x,
      y: t.clientY - startPan.y
    });
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
  };

  /* ======================================================
     BIND EVENTS
     ====================================================== */

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="chartbig-shell">

      {/* HEADER BAR */}
      <div className="chartbig-header">
        <div className="chartbig-title">
          <strong>{title}</strong>
        </div>

        <div className="chartbig-controls">
          <button type="button" onClick={zoomOut} title="Zoom Out">−</button>
          <button type="button" onClick={zoomIn} title="Zoom In">＋</button>
          <button type="button" onClick={resetView} title="Reset View">⟳</button>
        </div>
      </div>

      {/* MAIN STAGE */}
      <div
        ref={containerRef}
        className="chartbig-stage"
        onMouseDown={handlePanStart}
        onMouseMove={doPan}
        onMouseUp={stopPanning}
        onMouseLeave={stopPanning}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={contentRef}
          className="chartbig-content"
          style={{
            transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y + Y_OFFSET}px)) scale(${scale})`
          }}
        >
          <ToothChart
            toothStates={toothStates}
            surfacesMap={surfacesMap}
            flagsMap={flagsMap}
            movementsMap={movementsMap}
            selectedTeeth={selectedTeeth}
            onToothClick={onToothClick}
            readOnly={isLocked}
            autoFit={autoFit}
            animateStateTransitions={animateReplay}
            fitX={fitX}
            fitY={fitY}
            fitS={fitS}
            toothEvents={toothEvents}
          />
        </div>


      </div>

      {/* FOOTER HINT */}
      <div className="chartbig-footer">
        <span>🖱 Drag to pan • 🔍 Use controls to zoom</span>
      </div>

    </div>
  );
}
